import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, Plane, Languages, GraduationCap, Video, CheckCircle2, Plus, X, Lightbulb,
} from "lucide-react";

const COUNTRY_OPTIONS = [
  "India", "United Arab Emirates", "Singapore", "Saudi Arabia", "Qatar",
  "United Kingdom", "United States", "Canada", "Australia", "New Zealand",
  "Germany", "Netherlands", "Japan", "South Korea", "Vietnam", "Thailand",
  "Hong Kong", "Malaysia", "Oman", "Bahrain", "Kuwait",
];
const CURRICULA = ["IB", "IGCSE / Cambridge", "AP", "A-Levels", "American Curriculum", "British Curriculum", "Australian Curriculum", "CBSE", "ICSE"];
const PROFICIENCY = ["native", "fluent", "professional", "conversational"];

export default function GrowthReadiness() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newCountry, setNewCountry] = useState("");
  const [newLang, setNewLang] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/growth/global-readiness");
      setData(data);
    } catch { toast.error("Could not load readiness"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data: updated } = await api.put("/growth/global-readiness", patch);
      setData(updated);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading || !data) {
    return <Layout title="Global Readiness"><Skeleton className="h-64" /></Layout>;
  }

  const addCountry = () => {
    const v = newCountry.trim();
    if (!v) return;
    if ((data.preferred_countries || []).includes(v)) return;
    save({ preferred_countries: [...(data.preferred_countries || []), v] });
    setNewCountry("");
  };
  const removeCountry = (c) => save({ preferred_countries: (data.preferred_countries || []).filter((x) => x !== c) });

  const addLang = () => {
    const v = newLang.trim();
    if (!v) return;
    if ((data.other_languages || []).includes(v)) return;
    save({ other_languages: [...(data.other_languages || []), v] });
    setNewLang("");
  };
  const removeLang = (c) => save({ other_languages: (data.other_languages || []).filter((x) => x !== c) });

  return (
    <Layout title="Global Opportunity Readiness">
      <Button variant="ghost" onClick={() => navigate("/growth-studio")} className="mb-3 -ml-2" data-testid="readiness-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Growth Studio
      </Button>

      <div className="mb-6">
        <span className="tb-chip-sky inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Globe className="h-3 w-3" /> Global Opportunity Readiness
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Unlock international teaching opportunities
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Mark your passport, relocation, curricula and demo video status — recruiters use these signals to filter.
        </p>
      </div>

      {/* Score */}
      <div className="tb-card-feature p-5 sm:p-6 mb-5 grid grid-cols-1 sm:grid-cols-12 gap-5" data-testid="readiness-score-card">
        <div className="sm:col-span-3 flex items-center gap-4">
          <ScoreRing value={data.score} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Global readiness</p>
            <p className="font-display text-2xl tracking-tight font-medium">{labelFor(data.score)}</p>
            <p className="text-xs text-muted-foreground">{data.score} / 100</p>
          </div>
        </div>
        <div className="sm:col-span-9">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2 inline-flex items-center gap-1.5">
            <Lightbulb className="h-3 w-3" /> AI suggestions
          </p>
          <ul className="space-y-1.5">
            {(data.suggestions || []).map((s, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Travel + relocation */}
        <div className="tb-card p-5" data-testid="readiness-travel">
          <SectionHeading icon={Plane} title="Travel & relocation" />
          <ToggleRow
            label="Passport available?"
            checked={!!data.passport}
            onChange={(v) => save({ passport: v })}
            testid="readiness-passport"
          />
          <ToggleRow
            label="Ready to relocate"
            checked={!!data.relocation}
            onChange={(v) => save({ relocation: v })}
            testid="readiness-relocation"
          />
          <ToggleRow
            label="Visa support needed?"
            checked={!!data.visa_needed}
            onChange={(v) => save({ visa_needed: v })}
            testid="readiness-visa"
          />

          <div className="mt-4">
            <p className="text-[11px] font-medium mb-2">Preferred countries</p>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="readiness-countries">
              {(data.preferred_countries || []).map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-200 text-xs px-2.5 py-1">
                  {c}
                  <button onClick={() => removeCountry(c)} className="opacity-60 hover:opacity-100" aria-label={`Remove ${c}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {(data.preferred_countries || []).length === 0 && (
                <span className="text-xs text-muted-foreground">None yet.</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                list="growth-country-options"
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCountry()}
                placeholder="Add a country"
                className="h-9 rounded-xl"
                data-testid="readiness-country-input"
              />
              <datalist id="growth-country-options">
                {COUNTRY_OPTIONS.map((c) => <option key={c} value={c} />)}
              </datalist>
              <Button onClick={addCountry} disabled={busy || !newCountry.trim()} size="sm" className="rounded-full"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>

        {/* Languages + curricula */}
        <div className="tb-card p-5" data-testid="readiness-languages">
          <SectionHeading icon={Languages} title="Languages" />
          <div>
            <p className="text-[11px] font-medium mb-2">English proficiency</p>
            <div className="flex flex-wrap gap-1.5" data-testid="readiness-english">
              {PROFICIENCY.map((p) => (
                <button
                  key={p}
                  onClick={() => save({ english_proficiency: p })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition capitalize ${
                    data.english_proficiency === p ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                  }`}
                >{p}</button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <p className="text-[11px] font-medium mb-2">Other languages</p>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="readiness-other-langs">
              {(data.other_languages || []).map((l) => (
                <span key={l} className="inline-flex items-center gap-1 rounded-full bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-200 text-xs px-2.5 py-1">
                  {l}
                  <button onClick={() => removeLang(l)} className="opacity-60 hover:opacity-100" aria-label={`Remove ${l}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
              {(data.other_languages || []).length === 0 && <span className="text-xs text-muted-foreground">None yet.</span>}
            </div>
            <div className="flex gap-2">
              <Input
                value={newLang}
                onChange={(e) => setNewLang(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addLang()}
                placeholder="e.g. Hindi, Arabic, French"
                className="h-9 rounded-xl"
                data-testid="readiness-lang-input"
              />
              <Button onClick={addLang} disabled={busy || !newLang.trim()} size="sm" className="rounded-full"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>

        {/* International curricula */}
        <div className="tb-card p-5 lg:col-span-2" data-testid="readiness-curricula">
          <SectionHeading icon={GraduationCap} title="International curriculum experience" />
          <div className="flex flex-wrap gap-1.5">
            {CURRICULA.map((c) => {
              const active = (data.intl_curriculum_experience || []).includes(c);
              return (
                <button
                  key={c}
                  onClick={() => {
                    const next = active
                      ? data.intl_curriculum_experience.filter((x) => x !== c)
                      : [...(data.intl_curriculum_experience || []), c];
                    save({ intl_curriculum_experience: next });
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                  }`}
                  data-testid={`readiness-curr-${c.replace(/\W/g, "")}`}
                >
                  {c} {active && <CheckCircle2 className="inline h-3 w-3 ml-1" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Experience + demo */}
        <div className="tb-card p-5 lg:col-span-2" data-testid="readiness-experience">
          <SectionHeading icon={Video} title="Experience & demo" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-[11px] font-medium mb-1.5">Global teaching experience (years)</p>
              <Input
                type="number" min="0" max="60"
                defaultValue={data.global_experience_years || 0}
                onBlur={(e) => save({ global_experience_years: parseInt(e.target.value || "0", 10) })}
                className="h-10 rounded-xl"
                data-testid="readiness-global-years"
              />
            </div>
            <div className="flex items-end">
              <ToggleRow
                label="Ready for online teaching"
                checked={!!data.online_ready}
                onChange={(v) => save({ online_ready: v })}
                testid="readiness-online"
              />
            </div>
            <div>
              <p className="text-[11px] font-medium mb-1.5">Demo lesson video URL</p>
              <Input
                defaultValue={data.demo_video_url || ""}
                onBlur={(e) => save({ demo_video_url: e.target.value })}
                placeholder="https://youtube.com/…"
                className="h-10 rounded-xl"
                data-testid="readiness-demo-url"
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function SectionHeading({ icon: Icon, title }) {
  return (
    <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-1.5 mb-3">
      <Icon className="h-3 w-3" /> {title}
    </p>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-2 cursor-pointer">
      <span className="text-sm">{label}</span>
      <span
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => (e.key === " " || e.key === "Enter") && onChange(!checked)}
        className={`relative inline-block h-6 w-11 rounded-full transition ${checked ? "bg-primary" : "bg-muted"}`}
        data-testid={testid}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-5" : "left-0.5"}`} />
      </span>
    </label>
  );
}

function ScoreRing({ value }) {
  const r = 38, c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value || 0));
  const offset = c - (v / 100) * c;
  return (
    <div className="relative" data-testid="readiness-ring">
      <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r} fill="none"
          stroke="url(#g-grad)" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
        />
        <defs>
          <linearGradient id="g-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute inset-0 grid place-items-center font-display text-2xl font-medium tracking-tight">{v}</span>
    </div>
  );
}

function labelFor(s) {
  if (s >= 85) return "Global-ready";
  if (s >= 60) return "Almost there";
  if (s >= 30) return "Building up";
  return "Not yet ready";
}
